/**
 * E1. Лотерея: скретч-карта из шести клеток. Тап стирает клетку; когда
 * стёрты все шесть (или нажато «Стереть все»), билет куплен — исход
 * считает каркас, итог появится в журнале в конце года. Цена билета,
 * джекпот и шансы написаны на карточке из констант логики.
 */
import { t } from '@life/i18n';
import { LOTTERY_JACKPOT_P, LOTTERY_X1_P, LOTTERY_X10_P, type LotteryInput, type LotteryState } from '@life/minigames';
import { useState } from 'preact/hooks';
import { money } from '../lib/format';
import { useReveal } from './reveal';
import type { MinigameScreenProps } from './types';

export const LOTTERY_CELLS = 6;
export const LOTTERY_REVEAL_MS = 1500;

export function Lottery({ state, life, onInput }: MinigameScreenProps<LotteryInput, LotteryState>) {
  const [scratched, setScratched] = useState<boolean[]>(() => Array.from({ length: LOTTERY_CELLS }, () => false));
  const reveal = useReveal(LOTTERY_REVEAL_MS);
  const canBuy = life.money >= state.ticketPrice && state.ticketPrice > 0;
  const buy = () => onInput({ scratch: true });

  const scratch = (i: number) => {
    if (!canBuy || reveal.running) return;
    const next = scratched.map((s, k) => s || k === i);
    setScratched(next);
    if (next.every(Boolean)) buy();
  };

  const scratchAll = () => {
    if (!canBuy) return;
    reveal.begin(() => {
      setScratched(scratched.map(() => true));
      buy();
    });
  };

  return (
    <>
      <dl class="kv">
        <dt>{t('ui.minigame.lottery.ticket')}</dt>
        <dd class="mono">{money(state.ticketPrice)}</dd>
        <dt>{t('ui.minigame.lottery.jackpot')}</dt>
        <dd class="mono">{money(state.jackpot)}</dd>
      </dl>
      <p class="hint">
        {t('ui.minigame.lottery.odds', {
          jackpot: Math.round(1 / LOTTERY_JACKPOT_P).toLocaleString('ru-RU'),
          x10: Math.round(1 / LOTTERY_X10_P),
          x1: Math.round(1 / LOTTERY_X1_P),
        })}
      </p>
      <div class="scratch" role="group" aria-label={t('ui.minigame.lottery.card')}>
        {scratched.map((s, i) => (
          <button
            key={i}
            type="button"
            class={s ? 'cell done' : 'cell'}
            disabled={!canBuy || s}
            aria-pressed={s}
            aria-label={t('ui.minigame.lottery.cell', { n: i + 1 })}
            onClick={() => scratch(i)}
          >
            {s ? t('ui.minigame.lottery.scratched') : ''}
          </button>
        ))}
      </div>
      <p class="hint">{canBuy ? t('ui.minigame.lottery.hint') : t('ui.minigame.lottery.noMoney')}</p>
      {reveal.running ? (
        <button type="button" class="btn ghost" onClick={reveal.skip}>
          {t('ui.minigame.reveal.skip')}
        </button>
      ) : (
        <button type="button" class="btn accent block" disabled={!canBuy} onClick={scratchAll}>
          {canBuy ? t('ui.minigame.lottery.scratchAll') : t('ui.minigame.lottery.noMoney')}
        </button>
      )}
    </>
  );
}
